import { useModal } from "@/Contexts";
import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { useGlobalAlert } from "@/States/globalAlert";
import { getInfrastructureUrl } from "@/Utils/getInfrastructureUrl";
import { PointStatus } from "@/Utils/terminal";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { useConfig } from "@/api/hooks/useConfig";
import { useSlackNotifications } from "@/api/hooks/useSlackNotifications";
import ModalContainer from "@/components/modalContainer";

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { isEmpty } from "lodash";
import { POINT_STATUSES } from "server/coreApi/common/constants.js";

import { ContainerForm, ContainerFormData } from "../manage/containerForm";
import { useAccessParentsWithPoints } from "../manage/useAccessParentsWithPoints";
import { useAccessPoints } from "../manage/useAccessPoints";

export const AddContainerModal: React.FC<{
  accessParentId: string;
  selectedDepotId?: string | null;
}> = ({ accessParentId, selectedDepotId }) => {
  const { hideModal } = useModal();
  const navigate = useNavigate();
  const { terminalId: urlTerminalId } = useParams();
  const { getAccessParent } = useAccessParentsWithPoints();
  const { isLoadingAll, refetchAll } = useAccessPoints({ accessParentId });
  const { sendNewContainerSlackNotification } = useSlackNotifications();
  const { wasteTypes } = useCommonEntitiesStore();
  const { currentTerminal } = useTerminalsState();
  const { setGlobalAlert } = useGlobalAlert();
  const { config } = useConfig();

  const accessParent = getAccessParent(accessParentId);

  const { mutateAsync, isPending: isLoading } =
    trpc.accessPoints.create.useMutation({
      onSuccess: async (modifiedPoint) => {
        await refetchAll();

        setGlobalAlert({
          type: "success",
          message: "systemMessages:containerAdded",
        });
        const url = getInfrastructureUrl({
          terminalId: urlTerminalId,
          accessParentId: accessParent?.id,
          containerId: modifiedPoint.id,
        });
        navigate(url);

        sendNewContainerSlackNotification(
          modifiedPoint.name,
          selectedDepotId || accessParentId || "",
          modifiedPoint.id,
        );
        hideModal();
      },
      onError: (error) => {
        setGlobalAlert({
          type: "error",
          message: "errors:failedSave",
          instructions: error.message,
        });
      },
    });

  const formHandler = useForm<ContainerFormData>({
    defaultValues: {
      status: POINT_STATUSES[0],
      wasteTypeClassificationSystem:
        currentTerminal?.fractionDiscriminator ??
        config?.wasteTypeClassificationSystemId ??
        undefined,
    },
  });

  const {
    formState: { errors, isValid, isDirty },
    handleSubmit,
  } = formHandler;

  const onConfirmDisabled = useMemo(
    () => !isEmpty(errors) || !isValid,
    [errors, isValid],
  );

  if (!accessParentId || !accessParent) return null;

  const onSubmitSingle = async (containerFormData: ContainerFormData) => {
    const { containerName, wasteTypeClassificationSystem, wasteCode, status } =
      containerFormData;

    if (!wasteTypeClassificationSystem) return;

    const fractionDesc = wasteTypes?.find(({ id }) => id === wasteCode)?.name;

    await mutateAsync({
      name: containerName,
      fraction: wasteCode,
      fractionDesc: fractionDesc,
      fractionDiscriminator: wasteTypeClassificationSystem,
      status: status ?? PointStatus.ACTIVE,
      parentId: selectedDepotId || accessParentId,
    });
  };

  const onSubmit = handleSubmit((containerFormData) => {
    if (isLoading) return;
    onSubmitSingle(containerFormData);
  });

  return (
    <ModalContainer
      title="actions:newContainer"
      onConfirmText={"actions:save"}
      onConfirm={() => onSubmit()}
      onCancel={hideModal}
      onConfirmLoading={isLoadingAll}
      onConfirmDisabled={onConfirmDisabled}
      className="w-3/4 sm:w-1/2 lg:w-1/3"
      showDiscardModal={isDirty}
    >
      <div className="flex flex-col gap-4">
        <ContainerForm {...{ formHandler }} />
      </div>
    </ModalContainer>
  );
};
